import React, { useEffect, useState } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { CSVLink } from "react-csv";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import {
  FaPills,
  FaArrowCircleUp,
  FaArrowCircleDown,
  FaWarehouse,
  FaClock,
} from "react-icons/fa";

function MedicineReport() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      console.log("🔄 Fetching medicines from:", `${baseurl}getMedicines`);

      const res = await axios.get(`${baseurl}getMedicines`);

      console.log("✅ API Response:", res.data);

      // Some backends return an array, others wrap with { success, data }
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.data || [];

      if (data.length > 0) {
        setMedicines(data);
      } else {
        console.warn("⚠️ No medicines found in response:", res.data);
        Swal.fire("No Data", "No medicines found in the report.", "info");
      }
    } catch (err) {
      console.error("❌ API Fetch Error:", err);
      Swal.fire("Error", "Failed to fetch medicine report.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicines();
  }, []);

  // --- Filtered data ---
  const filtered = medicines.filter((m) =>
    m.name?.toLowerCase().includes(search.toLowerCase())
  );

  // --- Analytics summary ---
  const totalMedicines = medicines.length;
  const totalStock = medicines.reduce((sum, m) => sum + (m.stock || 0), 0);

  const totalIn = medicines.reduce(
    (sum, m) =>
      sum +
      (m.stock_movements?.filter((s) => s.transaction_type === "in")
        .reduce((a, b) => a + Number(b.quantity), 0) || 0),
    0
  );

  const totalOut = medicines.reduce(
    (sum, m) =>
      sum +
      (m.stock_movements?.filter((s) => s.transaction_type === "out")
        .reduce((a, b) => a + Number(b.quantity), 0) || 0),
    0
  );

  const soonToExpire = medicines.filter((m) => {
    const exp = new Date(m.expiry_date);
    const today = new Date();
    const diff = (exp - today) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff >= 0;
  }).length;

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="container py-0">
          {/* ===== Summary Cards ===== */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h4 className="fw-bold text-dark mb-3">💊 Medicine Stock Summary</h4>
              <div className="row g-3">
                {[
                  { label: "Total Medicines", value: totalMedicines, color: "card-gradient-blue", icon: <FaPills /> },
                  // { label: "Total Stock Units", value: totalStock, color: "card-gradient-green", icon: <FaWarehouse /> },
                  // { label: "Total Inflow", value: totalIn, color: "card-gradient-purple", icon: <FaArrowCircleUp /> },
                  // { label: "Total Outflow", value: totalOut, color: "card-gradient-orange", icon: <FaArrowCircleDown /> },
                  { label: "Expiring Soon (30 Days)", value: soonToExpire, color: "card-gradient-red", icon: <FaClock /> },
                ].map((item, i) => (
                  <div className="col-md-4 col-lg-3 col-6" key={i}>
                    <div className={`summary-card ${item.color}`}>
                      <div className="summary-icon">{item.icon}</div>
                      <div className="summary-info">
                        <h6 className="text-white">{item.label}</h6>
                        <h4 className="text-white">{item.value}</h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ===== Medicine Table ===== */}
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="fw-bold mb-0">📋 Medicine Inventory Report</h5>
                <div>
                  <input
                    type="text"
                    className="form-control d-inline-block me-2"
                    style={{ width: 250 }}
                    placeholder="Search medicine name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <CSVLink
                    data={filtered}
                    filename={"medicine-report.csv"}
                    className="btn btn-outline-primary btn-sm"
                  >
                    Export CSV
                  </CSVLink>
                </div>
              </div>

              {loading ? (
                <p className="text-center py-3">Loading medicines...</p>
              ) : filtered.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped align-middle text-center">
                    <thead className="table-header">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Stock</th>
                        <th>Unit</th>
                        <th>Cost ₹</th>
                        <th>Selling ₹</th>
                        <th>GST %</th>
                        <th>Expiry</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((m, i) => (
                        <tr key={m.id}>
                          <td>{i + 1}</td>
                          <td className="fw-semibold text-start">{m.name}</td>
                          <td>{m.category}</td>
                          <td className={m.stock === 0 ? "text-danger fw-bold" : ""}>
                            {m.stock}
                          </td>
                          <td>{m.unit}</td>
                          <td>₹{m.cost_price}</td>
                          <td>₹{m.selling_price}</td>
                          <td>{m.gst}%</td>
                          <td>
                            <span
                              className={
                                new Date(m.expiry_date) < new Date()
                                  ? "badge bg-danger"
                                  : "badge bg-success"
                              }
                            >
                              {new Date(m.expiry_date).toLocaleDateString("en-GB")}

                              {/* {m.expiry_date} */}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() =>
                                Swal.fire({
                                  title: `${m.name} - Stock Movements`,
                                  html: `
                                    <div style="max-height:300px;overflow-y:auto;text-align:left">
                                      <table class='table table-sm table-bordered'>
                                        <thead>
                                          <tr><th>Type</th><th>Qty</th><th>Reference</th><th>Date</th></tr>
                                        </thead>
                                        <tbody>
                                          ${m.stock_movements
                                            ?.map(
                                              (s) => `
                                                <tr>
                                                  <td>${
                                                    s.transaction_type === "in"
                                                      ? "<span class='text-success'>IN</span>"
                                                      : "<span class='text-danger'>OUT</span>"
                                                  }</td>
                                                  <td>${s.quantity}</td>
                                                  <td>${s.reference}</td>
                                                  <td>${s.created_at.split("T")[0]}</td>
                                                </tr>`
                                            )
                                            .join("")}
                                        </tbody>
                                      </table>
                                    </div>`,
                                  width: "600px",
                                })
                              }
                            >
                              View History
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-3">No medicines found.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default MedicineReport;
